import React from "react";
import { motion } from "framer-motion";
import {
  CalendarRange,
  GitBranch,
  LineChart,
  AlertTriangle,
  RefreshCcw,
  ClipboardList,
  ArrowUpRight,
} from "lucide-react";

const SERVICES = [
  {
    id: "scheduling",
    code: "S-01",
    video: "/videos/projectscheduling2.mp4",
    icon: CalendarRange,
    title: "Project Scheduling",
    desc: "Baseline schedule development in Primavera P6 and MS Project — sequenced, resource-loaded, and aligned to contract milestones.",
  },
  {
    id: "critical-path",
    code: "S-02",
    video: "/videos/criticalpath.mp4",
    icon: GitBranch,
    title: "Critical Path Analysis",
    desc: "Forensic CPM analysis to identify driving activities, near-critical paths, and float consumption across complex programs.",
  },
  {
    id: "schedule-dev",
    code: "S-03",
    video: "/videos/transforming.mp4",
    icon: ClipboardList,
    title: "Schedule Development",
    desc: "End-to-end schedule build-out: WBS architecture, activity coding, logic ties, and DCMA-14 compliant quality checks.",
  },
  {
    id: "progress",
    code: "S-04",
    icon: LineChart,
    video: "/videos/progresstracking.mp4",
    title: "Progress Tracking",
    desc: "Weekly and monthly updates with earned value, variance reporting, and executive dashboards your stakeholders trust.",
  },
  {
    id: "recovery",
    code: "S-05",
    video: "/videos/schedulerecovery.mp4",
    icon: RefreshCcw,
    title: "Schedule Recovery",
    desc: "When projects slip, we build defensible recovery schedules — acceleration scenarios, what-ifs, and mitigation playbooks.",
  },
  {
    id: "delay",
    code: "S-06",
    icon: AlertTriangle,
    video: "/videos/delaycontrol.mp4",
    title: "Delay & Controls Support",
    desc: "Time impact analysis, delay narratives, and full project controls support tailored to owners, GCs, and subcontractors.",
  },
];

export const Services = () => {
  return (
    <section
      data-testid="services-section"
      id="services"
      className="relative py-24 md:py-32 bg-[#04070D]"
    >
      <div className="absolute inset-0 grid-bg-fine opacity-40" />
      <div className="relative max-w-7xl mx-auto px-6 lg:px-10">
        {/* Section header */}
        <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-8 mb-16">
          <div>
            <div className="flex items-center gap-3 mb-4">
              <span className="w-10 h-px bg-[#00E5FF]" />
              <span className="font-mono-accent text-[11px] text-[#00E5FF]">
                Services / 01—06
              </span>
            </div>
            <h2 className="font-heading text-4xl sm:text-5xl lg:text-6xl text-[#F1F5F9] max-w-3xl leading-[1.05]">
              The full scheduling{" "}
              <span className="text-[#00E5FF]">stack</span> — built around your
              project.
            </h2>
          </div>
          <p className="text-[#94A3B8] max-w-md text-base leading-relaxed">
            From baseline through close-out, we deliver every layer of project
            controls — embedded with your team or fully outsourced.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-[#1E2A3B] border border-[#1E2A3B]">
          {SERVICES.map((s, i) => {
            const Icon = s.icon;
            return (
              <motion.div
                key={s.id}
                data-testid={`service-card-${s.id}`}
                initial={{ opacity: 0, y: 24 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: i * 0.06 }}
                className="group relative bg-[#04070D] p-8 lg:p-10 transition-colors duration-300 hover:bg-[#0A101D]"
              >
                {/* Background Video */}
                <video
                  className="absolute inset-0 w-full h-full object-cover opacity-20 group-hover:opacity-40 transition-opacity duration-500"
                  autoPlay
                  muted
                  loop
                  playsInline
                >
                  <source src={s.video} type="video/mp4" />
                </video>

                {/* Optional animated glow */}
                <div className="absolute inset-0 bg-[#00E5FF]/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                {/* Top row: code + icon */}
                <div className="flex items-start justify-between mb-10">
                  <span className="font-mono-accent text-[10px] text-[#4A6E8C]">
                    {s.code}
                  </span>
                  <div className="w-10 h-10 flex items-center justify-center border border-[#1E2A3B] group-hover:border-[#FFB300] transition-colors">
                    <Icon
                      size={18}
                      className="text-[#00E5FF] group-hover:text-[#FFB300] transition-colors"
                      strokeWidth={1.5}
                    />
                  </div>
                </div>

                <h3 className="font-heading text-2xl text-[#F1F5F9] mb-4 leading-tight">
                  {s.title}
                </h3>
                <p className="text-sm text-[#94A3B8] leading-relaxed mb-8">
                  {s.desc}
                </p>

                <div className="flex items-center gap-2 font-mono-accent text-[10px] text-[#4A6E8C] group-hover:text-[#FFB300] transition-colors">
                  <span>Capability detail</span>
                  <ArrowUpRight size={12} />
                </div>

                {/* Hover trace line */}
                <span className="absolute bottom-0 left-0 h-px w-0 bg-[#FFB300] group-hover:w-full transition-all duration-500" />
              </motion.div>
            );
          })}
        </div>

        {/* Approach strip */}
        <div
          id="approach"
          className="mt-24 border border-[#1E2A3B] glass-panel p-8 lg:p-12"
        >
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1">
              <div className="font-mono-accent text-[11px] text-[#FFB300] mb-3">
                / Our Approach
              </div>
              <h3 className="font-heading text-3xl text-[#F1F5F9] leading-tight">
                Four phases. Zero ambiguity.
              </h3>
            </div>
            <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-px bg-[#1E2A3B]">
              {[
                { n: "01", t: "Discover", d: "Scope, contract & WBS review" },
                { n: "02", t: "Baseline", d: "Build, validate & DCMA-14 check" },
                { n: "03", t: "Monitor", d: "Updates, EVM & variance reports" },
                { n: "04", t: "Steer", d: "Recovery, delay & exec reporting" },
              ].map((p) => (
                <div key={p.n} className="bg-[#0A101D] p-6">
                  <div className="font-mono-accent text-[10px] text-[#00E5FF] mb-3">
                    {p.n}
                  </div>
                  <div className="font-heading text-lg text-[#F1F5F9] mb-2">
                    {p.t}
                  </div>
                  <div className="text-xs text-[#94A3B8] leading-relaxed">
                    {p.d}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Services;
